import { getFinancialYearLabels } from "../../brsrFinancialYearLabels.js";
import { SHEET_NAME as OTHER_RETIREMENT_BENEFITS_SHEET } from "./otherRetirementBenefitsSheet.js";
import {
  BENEFIT_KEYS,
  BODY_FONT,
  COL_D,
  COL_E,
  COL_F,
  COL_G,
  COL_H,
  COL_I,
  COL_J,
  COL_K,
  COL_L,
  COL_M,
  COL_N,
  COL_O,
  COL_P,
  HEADER_FILL,
  LINKED_FILL,
  NA_FILL,
  TITLE_FONT,
  VALUE_FILL,
  applyBorderSpecs,
  borderFromSpecs,
  dpSectionBorders,
  dpTitleBorders,
  formatNumber,
  formatPercent,
  formatRatio,
  mergeBlock,
  outerMergeBorders,
  padMergeRow,
  parseBorderSpec,
  safeText,
  setCell,
  setEdgeP,
  setGrey,
  setGreyRange,
  setLinked,
  setPlain,
  setPrinciple3ColumnWidths,
  setWhite,
  setupPrinciple3SheetView,
  toYesNo,
  hideRowsThrough,
} from "./principle3SheetHelpers.js";

const PRINCIPLE_TITLE =
  "PRINCIPLE 3 Businesses should respect and promote the well-being of all employees, including those in their value chains";

const RETIREMENT_ROWS = [
  ["PF", "pf"],
  ["Gratuity", "gratuity"],
  ["ESI", "ESI"],
];

const SAFETY_INCIDENTS = [
  ["Lost Time Injury Frequency Rate (LTIFR) (per one million-person hours worked)", "lostTimeInjuryFrequencyRate"],
  ["Total recordable work-related injuries", "totalRecordableInjuries"],
  ["No. of fatalities", "numberOfFatalities"],
  ["High consequence work related injury or ill-health (excluding fatalities)", "highConsequenceInjuries"],
];

const GRIEVANCE_SEGMENTS = [
  ["Permanent Workers", "permanentWorkers"],
  ["Other than Permanent Workers", "otherThanPermanentWorkers"],
  ["Permanent Employees", "permanentEmployees"],
  ["Other than Permanent Employees", "otherThanPermanentEmployees"],
];

const THIN_ALL = "tttt";

function writeMergedLabel(ws, row, startCol, endCol, text, specs, options = {}) {
  for (let col = startCol; col <= endCol; col += 1) {
    setCell(ws, row, col, col === startCol ? text : "", {
      bold: options.bold,
      font: options.font,
      align: options.align || "left",
      vertical: options.vertical || "middle",
      fill: options.fill,
      bordered: false,
      border: borderFromSpecs(specs, col),
    });
  }
  padMergeRow(ws, row, startCol, endCol, specs);
  // Restore borders on trailing non-merged cols from specs
  if (specs?.length) {
    for (let col = endCol + 1; col <= COL_P; col += 1) {
      const border = borderFromSpecs(specs, col);
      if (border) ws.getCell(row, col).border = border;
    }
  }
  const edgeSpec = options.edgeSpec || (endCol < COL_P ? specs[COL_P - COL_D] || "---m" : null);
  if (edgeSpec) setEdgeP(ws, row, edgeSpec, { grey: !!options.edgeGrey });
  else if (options.edgeGrey && endCol >= COL_P) {
    ws.getCell(row, COL_P).fill = NA_FILL;
  }
  if (options.height != null) ws.getRow(row).height = options.height;
}

function addPrincipleTitle(ws) {
  const specs = dpTitleBorders();
  for (let col = COL_D; col <= COL_P; col += 1) {
    setCell(ws, 8, col, col === COL_D ? PRINCIPLE_TITLE : "", {
      font: TITLE_FONT,
      align: "center",
      fill: HEADER_FILL,
      bordered: false,
      border: borderFromSpecs(specs, col),
    });
  }
  padMergeRow(ws, 8, COL_D, COL_P, specs);
  ws.getRow(8).height = 30;
}

function addSectionBand(ws, row, title) {
  const specs = dpSectionBorders();
  for (let col = COL_D; col <= COL_P; col += 1) {
    setCell(ws, row, col, col === COL_D ? title : "", {
      font: { name: "Calibri", size: 12, bold: true },
      align: "center",
      fill: NA_FILL,
      bordered: false,
      border: borderFromSpecs(specs, col),
    });
  }
  padMergeRow(ws, row, COL_D, COL_P, specs);
  ws.getRow(row).height = 20.15;
  return row + 1;
}

function benefitNums(section, benefitKey, prefix) {
  const benefit = section?.[benefitKey] || {};
  if (prefix === "male") {
    return { num: formatNumber(benefit.male), pct: formatRatio(benefit.malePercentage) };
  }
  if (prefix === "female") {
    return { num: formatNumber(benefit.female), pct: formatRatio(benefit.femalePercentage) };
  }
  if (prefix === "other") {
    return {
      num: formatNumber(benefit.other ?? benefit.others),
      pct: formatRatio(benefit.otherPercentage ?? benefit.othersPercentage),
    };
  }
  if (prefix === "total") {
    const male = Number(benefit.male) || 0;
    const female = Number(benefit.female) || 0;
    const other = Number(benefit.other ?? benefit.others) || 0;
    const totalFromData = benefit.total != null && benefit.total !== "" ? Number(benefit.total) : null;
    const total = totalFromData != null && !Number.isNaN(totalFromData) ? totalFromData : male + female + other;
    if (!total && !male && !female && !other) return { num: "", pct: "" };
    const pct =
      benefit.totalPercentage != null && benefit.totalPercentage !== ""
        ? formatRatio(benefit.totalPercentage)
        : total
          ? formatRatio((total / total) * 100)
          : "";
    return { num: formatNumber(total || male + female + other), pct };
  }
  return { num: "", pct: "" };
}

function addWellBeingHeader(ws, startRow, coveredLabel) {
  // R11 / R25
  const r1 = startRow;
  const r2 = startRow + 1;
  const r3 = startRow + 2;

  mergeBlock(ws, r1, COL_D, r3, COL_D, "Category", {
    align: "center",
    bordered: false,
    border: parseBorderSpec("tmtt"),
  });
  mergeBlock(ws, r1, COL_E, r1, COL_O, coveredLabel, {
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  setEdgeP(ws, r1, "---m", { grey: true });

  mergeBlock(ws, r2, COL_E, r3, COL_E, "Total (A)", {
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });

  const pairs = [
    [COL_F, "Health insurance", "Number (B)", "% (B / A)"],
    [COL_H, "Accident insurance", "Number (C)", "% (C / A)"],
    [COL_J, "Maternity benefits", "Number (D)", "% (D / A)"],
    [COL_L, "Paternity Benefits", "Number (E)", "% (E / A)"],
    [COL_N, "Day Care facilities", "Number (F)", "% (F / A)"],
  ];
  pairs.forEach(([col, label, numLabel, pctLabel]) => {
    mergeBlock(ws, r2, col, r2, col + 1, label, {
      align: "center",
      bordered: false,
      border: parseBorderSpec(THIN_ALL),
    });
    setCell(ws, r3, col, numLabel, {
      align: "center",
      bordered: false,
      border: parseBorderSpec(THIN_ALL),
    });
    setCell(ws, r3, col + 1, pctLabel, {
      align: "center",
      bordered: false,
      border: parseBorderSpec(THIN_ALL),
    });
  });

  for (const r of [r2, r3]) {
    for (let col = COL_D; col <= COL_O; col += 1) {
      const cell = ws.getCell(r, col);
      if (!cell.border) cell.border = parseBorderSpec(THIN_ALL);
      if (!cell.font) cell.font = BODY_FONT;
    }
    setEdgeP(ws, r, "---m", { grey: true });
    ws.getRow(r).height = 15;
  }
  return startRow + 3;
}

function addWellBeingBand(ws, row, label) {
  const specs = outerMergeBorders(COL_O, {
    first: "tmt-",
    middle: "t---",
    last: "t--t",
    edge: "---m",
  });
  writeMergedLabel(ws, row, COL_D, COL_O, label, specs, { bold: false });
  ws.getCell(row, COL_D).fill = NA_FILL;
  ws.getCell(row, COL_D).alignment = { horizontal: "left", vertical: "middle", wrapText: true };
  ws.getCell(row, COL_E).fill = undefined;
  ws.getCell(row, COL_E).alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  ws.getCell(row, COL_I).fill = undefined;
  ws.getCell(row, COL_I).alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  for (let col = COL_F; col <= COL_H; col += 1) {
    ws.getCell(row, col).fill = undefined;
  }
  for (let col = COL_J; col <= COL_O; col += 1) {
    ws.getCell(row, col).fill = undefined;
  }
  // Keep outer box on merge master (don't push per-slave borders — ExcelJS collapses them)
  const startBorder = borderFromSpecs(specs, COL_D) || {};
  const endBorder = borderFromSpecs(specs, COL_O) || {};
  ws.getCell(row, COL_D).border = {
    top: startBorder.top,
    left: startBorder.left,
    bottom: startBorder.bottom,
    right: endBorder.right,
  };
  setEdgeP(ws, row, "---m", { grey: true });
  return row + 1;
}

function addWellBeingDataRows(ws, row, measures, config) {
  const { sectionKey, maleCountKey, femaleCountKey } = config;
  const section = measures?.[sectionKey] || {};
  const genderRows = [
    {
      label: "Male",
      prefix: "male",
      total: measures?.maleEmployees?.[maleCountKey],
      // D E F G H I J K L M N O P — matches reference wellbeing rows
      borders: ["tmt-", "tttt", "t-t-", "tttt", "t-t-", "tttt", "tttt", "tttt", "t---", "tttt", "t-t-", "tttt", "---m"],
    },
    {
      label: "Female",
      prefix: "female",
      total: measures?.femaleEmployees?.[femaleCountKey],
      borders: ["tmt-", "tttt", "t-t-", "tttt", "t-t-", "tttt", "--t-", "tttt", "tttt", "tttt", "t-t-", "tttt", "---m"],
    },
    {
      label: "Other",
      prefix: "other",
      total: measures?.otherEmployees?.[maleCountKey] ?? measures?.otherEmployees?.[femaleCountKey],
      borders: ["tmt-", "tttt", "t---", "tttt", "t---", "tttt", "t---", "tttt", "----", "tttt", "t---", "tttt", "---m"],
    },
    {
      label: "Total",
      prefix: "total",
      total:
        (Number(measures?.maleEmployees?.[maleCountKey]) || 0) +
        (Number(measures?.femaleEmployees?.[femaleCountKey]) || 0) +
        (Number(measures?.otherEmployees?.[maleCountKey]) || 0),
      borders: ["tmt-", "tttt", "tttt", "-ttt", "tttt", "-ttt", "tttt", "-ttt", "tttt", "-ttt", "tttt", "-ttt", "---m"],
    },
  ];

  genderRows.forEach(({ label, prefix, total, borders }) => {
    const isTotal = prefix === "total";
    setCell(ws, row, COL_D, label, {
      align: "left",
      bordered: false,
      border: parseBorderSpec(borders[0]),
    });

    const totalVal =
      total != null && total !== "" && Number(total) !== 0
        ? formatNumber(total)
        : isTotal && total === 0
          ? ""
          : formatNumber(total);

    setCell(ws, row, COL_E, isTotal ? "" : totalVal, {
      align: "center",
      fill: isTotal ? NA_FILL : LINKED_FILL,
      bordered: false,
      border: parseBorderSpec(borders[1]),
    });

    let col = COL_F;
    let bi = 2;
    BENEFIT_KEYS.forEach(([key]) => {
      const { num, pct } = benefitNums(section, key, prefix);
      const maternityNa = prefix === "male" && key === "maternityBenefits";
      const paternityNa = prefix === "female" && key === "paternityBenefits";
      const pairNa = isTotal || maternityNa || paternityNa;
      const numBorder = parseBorderSpec(borders[bi]);
      const pctBorder = parseBorderSpec(borders[bi + 1]);

      if (pairNa) {
        setCell(ws, row, col, "", {
          align: "center",
          fill: NA_FILL,
          bordered: false,
          border: numBorder,
        });
        setCell(ws, row, col + 1, "", {
          align: "center",
          fill: NA_FILL,
          bordered: false,
          border: pctBorder,
        });
      } else {
        setWhite(ws, row, col, num, numBorder);
        setCell(ws, row, col + 1, pct, {
          align: "center",
          fill: NA_FILL,
          bordered: false,
          border: pctBorder,
        });
      }
      col += 2;
      bi += 2;
    });

    setEdgeP(ws, row, borders[12], { grey: true });
    row += 1;
  });

  return row;
}

function addWellBeingBlock(ws, row, measures, config) {
  row = addWellBeingBand(ws, row, config.blockLabel);
  row = addWellBeingDataRows(ws, row, measures, config);
  return row;
}

function addSpendingSection(ws, row, essential, fyCurrent, fyPrevious) {
  const qSpecs = outerMergeBorders(COL_J, {
    first: "tmtt",
    middle: "-ttt",
    last: "-ttt",
    edge: "t--m",
  });
  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_J,
    "C. Spending on measures towards well-being of employees and workers (including permanent and other than permanent) in the following format:",
    qSpecs,
    { bold: true, height: 15 }
  );
  setGreyRange(ws, row, COL_K, COL_P);
  setEdgeP(ws, row, "t--m", { grey: true });
  row += 1;

  const fySpecs = [
    "tmtt",
    "tt-t",
    "tt-t",
    "tt-t",
    "tt-t",
    "tt-t",
    "tt-t",
    "-t--",
    "----",
    "----",
    "----",
    "----",
    "---m",
  ];
  setCell(ws, row, COL_D, "", { bordered: false, border: borderFromSpecs(fySpecs, COL_D) });
  for (let col = COL_E; col <= COL_G; col += 1) {
    setCell(ws, row, col, col === COL_E ? fyCurrent : "", {
      bold: true,
      align: "center",
      bordered: false,
      border: borderFromSpecs(fySpecs, col),
    });
  }
  for (let col = COL_H; col <= COL_J; col += 1) {
    setCell(ws, row, col, col === COL_H ? fyPrevious : "", {
      bold: true,
      align: "center",
      bordered: false,
      border: borderFromSpecs(fySpecs, col),
    });
  }
  padMergeRow(ws, row, COL_E, COL_G, fySpecs);
  padMergeRow(ws, row, COL_H, COL_J, fySpecs);
  setGreyRange(ws, row, COL_K, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  const costCurrent =
    essential?.wellBeingCostCurrent ??
    essential?.costIncurredWellBeingCurrent ??
    essential?.wellBeingMeasures?.costCurrent;
  const costPrevious =
    essential?.wellBeingCostPrevious ??
    essential?.costIncurredWellBeingPrevious ??
    essential?.wellBeingMeasures?.costPrevious;
  const revenueCurrent =
    essential?.totalRevenueCurrent ?? essential?.wellBeingMeasures?.revenueCurrent;
  const revenuePrevious =
    essential?.totalRevenuePrevious ?? essential?.wellBeingMeasures?.revenuePrevious;
  const pctCurrent = essential?.wellBeingMeasuresCurrent;
  const pctPrevious = essential?.wellBeingMeasuresPrevious;

  const spendRows = [
    {
      label:
        "i) Cost incurred on wellbeing measures\n(well-being measures means well-being of employees and workers (including male, female, permanent and other than permanent employees and workers)",
      current: costCurrent,
      previous: costPrevious,
      linked: false,
      height: 101.5,
    },
    {
      label: "ii) Total revenue of the company",
      current: revenueCurrent,
      previous: revenuePrevious,
      linked: false,
    },
    {
      label: "iii) Cost incurred on wellbeing measures as a % of total revenue of the company",
      current: pctCurrent,
      previous: pctPrevious,
      linked: true,
      height: 43.5,
      asRatio: true,
    },
  ];

  spendRows.forEach((item) => {
    const specs = item.linked
      ? ["tmt-", THIN_ALL, THIN_ALL, THIN_ALL, THIN_ALL, THIN_ALL, THIN_ALL, "--t-", "--t-", "--t-", "--t-", "--t-", "--tm"]
      : ["tmt-", THIN_ALL, THIN_ALL, THIN_ALL, "tt-t", "tt-t", "tt-t", "----", "----", "----", "----", "----", "---m"];
    setCell(ws, row, COL_D, item.label, {
      align: "left",
      vertical: "top",
      bordered: false,
      border: borderFromSpecs(specs, COL_D),
    });
    const cur = item.asRatio ? formatRatio(item.current) : formatNumber(item.current);
    const prev = item.asRatio ? formatRatio(item.previous) : formatNumber(item.previous);
    for (let col = COL_E; col <= COL_G; col += 1) {
      setCell(ws, row, col, col === COL_E ? cur : "", {
        align: "center",
        fill: col === COL_E ? (item.linked ? LINKED_FILL : VALUE_FILL) : undefined,
        bordered: false,
        border: borderFromSpecs(specs, col),
      });
    }
    for (let col = COL_H; col <= COL_J; col += 1) {
      setCell(ws, row, col, col === COL_H ? prev : "", {
        align: "center",
        fill: col === COL_H ? (item.linked ? LINKED_FILL : VALUE_FILL) : undefined,
        bordered: false,
        border: borderFromSpecs(specs, col),
      });
    }
    padMergeRow(ws, row, COL_E, COL_G, specs);
    padMergeRow(ws, row, COL_H, COL_J, specs);
    setGreyRange(ws, row, COL_K, COL_P);
    setEdgeP(ws, row, item.linked ? "--tm" : "---m", { grey: true });
    if (item.height) ws.getRow(row).height = item.height;
    row += 1;
  });

  return row;
}

function addRetirementSection(ws, row, essential, fyCurrent, fyPrevious) {
  const qSpecs = dpSectionBorders().map((s, i) => {
    if (i === 0) return "tmt-";
    if (i === 12) return "t-tm";
    return "--t-";
  });
  // Reference uses tmt- / --t- / t-tm with bold
  writeMergedLabel(ws, row, COL_D, COL_P, "2. Details of retirement benefits", qSpecs, {
    bold: true,
  });
  row += 1;

  mergeBlock(ws, row, COL_D, row + 1, COL_D, "Benefits", {
    align: "center",
    bordered: false,
    border: parseBorderSpec("tmtt"),
  });
  mergeBlock(ws, row, COL_E, row, COL_J, fyCurrent, {
    bold: true,
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, row, COL_K, row, COL_P, fyPrevious, {
    bold: true,
    align: "center",
    bordered: false,
    border: parseBorderSpec("tttm"),
  });
  applyBorderSpecs(ws, row, Array(13).fill(THIN_ALL));
  row += 1;

  const subHeaders = [
    [COL_E, "No.of employees covered as a % of total employees"],
    [COL_G, "No.of workers covered as a % of total workers"],
    [COL_I, "Deducted and deposited with the authority (Y/N/N.A.)"],
    [COL_K, "No.of employees covered as a % of total employees"],
    [COL_M, "No.of workers covered as a % of total workers"],
    [COL_O, "Deducted and deposited with the authority (Y/N/N.A.)"],
  ];
  subHeaders.forEach(([col, label]) => {
    mergeBlock(ws, row, col, row, col + 1, label, {
      align: "center",
      bordered: false,
      border: parseBorderSpec(col === COL_O ? "tttm" : THIN_ALL),
    });
  });
  ws.getRow(row).height = 37.5;
  row += 1;

  const retirement = essential?.retirementBenefits || {};
  RETIREMENT_ROWS.forEach(([label, key]) => {
    const current = retirement?.currentFinancialYear?.[key] || {};
    const previous = retirement?.previousFinancialYear?.[key] || {};
    setCell(ws, row, COL_D, label, {
      align: "left",
      bordered: false,
      border: parseBorderSpec("tmtt"),
    });
    const pairs = [
      [COL_E, formatPercent(current?.employeesCovered)],
      [COL_G, formatPercent(current?.workersCovered)],
      [COL_I, safeText(current?.deductedDeposited)],
      [COL_K, formatPercent(previous?.employeesCovered)],
      [COL_M, formatPercent(previous?.workersCovered)],
      [COL_O, safeText(previous?.deductedDeposited)],
    ];
    pairs.forEach(([col, value]) => {
      setWhite(ws, row, col, value, parseBorderSpec("tttt"));
      setCell(ws, row, col + 1, "", {
        bordered: false,
        border: parseBorderSpec(col === COL_O ? "tttm" : "tttt"),
      });
      const pairSpecs = Array.from({ length: 13 }, (_, i) => {
        const c = COL_D + i;
        if (c === col) return "tttt";
        if (c === col + 1) return col === COL_O ? "tttm" : "tttt";
        return null;
      });
      padMergeRow(ws, row, col, col + 1, pairSpecs);
      ws.getCell(row, col).border = parseBorderSpec("tttt");
      ws.getCell(row, col + 1).border = parseBorderSpec(col === COL_O ? "tttm" : "tttt");
    });
    row += 1;
  });

  setCell(ws, row, COL_D, "Others – please specify", {
    align: "left",
    bordered: false,
    border: parseBorderSpec("tmtt"),
  });
  setCell(ws, row, COL_E, "Add Details", {
    align: "center",
    link: `#'${OTHER_RETIREMENT_BENEFITS_SHEET}'!D8`,
    bordered: false,
    border: parseBorderSpec("----"),
  });
  setCell(ws, row, COL_F, "", {
    bordered: false,
    border: parseBorderSpec("----"),
  });
  if (COL_E < COL_F) ws.mergeCells(row, COL_E, row, COL_F);
  ws.getCell(row, COL_E).border = parseBorderSpec("----");
  ws.getCell(row, COL_F).border = parseBorderSpec("----");
  // Cells after Add Details are grey (G:P)
  const otherGreySpecs = [
    [COL_G, "ttt-", "t-t-"],
    [COL_I, "t-t-", "t-t-"],
    [COL_K, "t-t-", "t-t-"],
    [COL_M, "t-t-", "t-t-"],
    [COL_O, "t-t-", "t-tm"],
  ];
  otherGreySpecs.forEach(([col, leftSpec, rightSpec]) => {
    setCell(ws, row, col, "", {
      fill: NA_FILL,
      bordered: false,
      border: parseBorderSpec(leftSpec),
    });
    setCell(ws, row, col + 1, "", {
      fill: NA_FILL,
      bordered: false,
      border: parseBorderSpec(rightSpec),
    });
    if (col < col + 1) ws.mergeCells(row, col, row, col + 1);
    ws.getCell(row, col).border = parseBorderSpec(leftSpec);
    ws.getCell(row, col + 1).border = parseBorderSpec(rightSpec);
  });
  setEdgeP(ws, row, "t-tm", { grey: true });
  return row + 1;
}

function addYesNoRow(ws, row, label, value, labelEnd, valueCol, options = {}) {
  const specs = [];
  for (let col = COL_D; col <= COL_P; col += 1) {
    if (col === COL_D) specs.push(options.first || "tmtt");
    else if (col < labelEnd) specs.push(options.mid || THIN_ALL);
    else if (col === labelEnd) specs.push(options.labelLast || "tttt");
    else if (col === valueCol) specs.push("ttt-");
    else if (col > valueCol && col < COL_P) specs.push("----");
    else if (col === COL_P) specs.push("---m");
    else specs.push("----");
  }
  writeMergedLabel(ws, row, COL_D, labelEnd, label, specs, {
    height: options.height,
  });
  if (options.greyValue) {
    setGrey(ws, row, valueCol, borderFromSpecs(specs, valueCol));
  } else if (options.link) {
    setCell(ws, row, valueCol, value || "Add Details", {
      align: "center",
      link: options.link,
      fill: VALUE_FILL,
      bordered: false,
      border: borderFromSpecs(specs, valueCol),
    });
  } else {
    setWhite(ws, row, valueCol, value, borderFromSpecs(specs, valueCol));
  }
  // Trailing cells after the value are grey
  if (options.greyAfter !== false) {
    if (valueCol + 1 <= COL_P) {
      setCell(ws, row, valueCol + 1, "", {
        fill: NA_FILL,
        bordered: false,
        border: parseBorderSpec(valueCol + 1 === COL_P ? "---m" : "tttt"),
      });
    }
    if (valueCol + 2 <= COL_P) {
      setGreyRange(ws, row, valueCol + 2, COL_P, "----");
    }
    setEdgeP(ws, row, "---m", { grey: true });
  }
  return row + 1;
}

function addReturnRetention(ws, row, block) {
  const specs56 = [
    "tmtt",
    "tttt",
    "tttt",
    "tttt",
    "tttt",
    "tttt",
    "tttt",
    "tttt",
    "tttt",
    "tttt",
    "tttt",
    "tttt",
    "tttm",
  ];
  setCell(ws, row, COL_D, "", {
    fill: NA_FILL,
    bordered: false,
    border: borderFromSpecs(specs56, COL_D),
  });
  mergeBlock(ws, row, COL_E, row, COL_H, "Permanent employees", {
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, row, COL_I, row, COL_L, "Permanent workers", {
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  ws.getCell(row, COL_E).fill = undefined;
  ws.getCell(row, COL_E).alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  ws.getCell(row, COL_I).fill = undefined;
  ws.getCell(row, COL_I).alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  for (let col = COL_F; col <= COL_H; col += 1) ws.getCell(row, col).fill = undefined;
  for (let col = COL_J; col <= COL_L; col += 1) ws.getCell(row, col).fill = undefined;
  setGreyRange(ws, row, COL_M, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  setCell(ws, row, COL_D, "Gender", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("tmtt"),
  });
  const sub = [
    [COL_E, "Return to work rate"],
    [COL_G, "Retention rate"],
    [COL_I, "Return to work rate"],
    [COL_K, "Retention rate"],
  ];
  sub.forEach(([col, label]) => {
    mergeBlock(ws, row, col, row, col + 1, label, {
      align: "center",
      fill: NA_FILL,
      bordered: false,
      border: parseBorderSpec(THIN_ALL),
    });
    ws.getCell(row, col).fill = NA_FILL;
    ws.getCell(row, col + 1).fill = NA_FILL;
  });
  setGreyRange(ws, row, COL_M, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  const emp = block?.permanentEmployees || {};
  const work = block?.permanentWorkers || {};
  ["Male", "Female", "Other", "Total"].forEach((label) => {
    const key = label.toLowerCase();
    setCell(ws, row, COL_D, label, {
      align: "left",
      bordered: false,
      border: parseBorderSpec("tmt-"),
    });
    const vals = [
      [COL_E, formatPercent(emp?.[key]?.returnToWorkRate)],
      [COL_G, formatPercent(emp?.[key]?.retentionRate)],
      [COL_I, formatPercent(work?.[key]?.returnToWorkRate)],
      [COL_K, formatPercent(work?.[key]?.retentionRate)],
    ];
    vals.forEach(([col, value]) => {
      setWhite(ws, row, col, value, parseBorderSpec("tttt"));
      setCell(ws, row, col + 1, "", {
        bordered: false,
        border: parseBorderSpec("tttt"),
      });
      padMergeRow(ws, row, col, col + 1);
      ws.getCell(row, col).border = parseBorderSpec("tttt");
      ws.getCell(row, col + 1).border = parseBorderSpec("tttt");
    });
    setGreyRange(ws, row, COL_M, COL_P, "----");
    setEdgeP(ws, row, "---m", { grey: true });
    row += 1;
  });

  return row;
}

function addMembershipTable(ws, row, block, fyCurrent, fyPrevious) {
  mergeBlock(ws, row, COL_D, row + 1, COL_D, "Category", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("tmtt"),
  });
  mergeBlock(ws, row, COL_E, row, COL_J, fyCurrent, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, row, COL_K, row, COL_P, fyPrevious, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("tttm"),
  });
  for (let col = COL_D; col <= COL_P; col += 1) ws.getCell(row, col).fill = NA_FILL;
  applyBorderSpecs(ws, row, Array(13).fill(THIN_ALL));
  row += 1;

  const headers = [
    [COL_E, "Total employees/workers in respective category (A)"],
    [COL_G, "No.of employees / workers in respective category, who are part of association(s) or Union (B)"],
    [COL_I, "% (B / A)"],
    [COL_K, "Total employees / workers in respective category (C)"],
    [COL_M, "No.of employees /workers in respective category, who are part of association(s) or Union (D)"],
    [COL_O, "% (D / C)"],
  ];
  headers.forEach(([col, label]) => {
    mergeBlock(ws, row, col, row, col + 1, label, {
      align: "center",
      fill: NA_FILL,
      bordered: false,
      border: parseBorderSpec(col === COL_O ? "tt-m" : "tt-t"),
    });
    ws.getCell(row, col).fill = NA_FILL;
    ws.getCell(row, col + 1).fill = NA_FILL;
  });
  ws.getCell(row, COL_D).fill = NA_FILL;
  ws.getRow(row).height = 45.75;
  row += 1;

  const groups = [
    {
      band: "Total Permanent Employees",
      data: block?.permanentEmployees,
      keys: {
        male: ["totalMaleEmployees", "NoOfMaleEmployeesCategory", "totalMaleEmployeesPercentage"],
        female: ["totalFemaleEmployees", "NoOfFemaleEmployeesCategory", "totalfemaleEmployeesPercentage"],
        other: ["totalOtherEmployees", "NoOfOtherEmployeesCategory", "totalOtherEmployeesPercentage"],
      },
    },
    {
      band: "Total Permanent Workers",
      data: block?.permanentWorkers,
      keys: {
        male: ["totalMaleEmployees", "NoOfMaleEmployeesCategory", "totalMaleEmployeesPercentage"],
        female: ["totalFemaleEmployees", "NoOfFemaleEmployeesCategory", "totalfemaleEmployeesPercentage"],
        other: ["totalOtherEmployees", "NoOfOtherEmployeesCategory", "totalOtherEmployeesPercentage"],
      },
    },
  ];

  groups.forEach((group) => {
    const bandSpecs = outerMergeBorders(COL_P, {
      first: "tmt-",
      middle: THIN_ALL,
      last: "tttm",
      edge: "tttm",
    });
    writeMergedLabel(ws, row, COL_D, COL_D, group.band, bandSpecs);
    setGreyRange(ws, row, COL_E, COL_P);
    setEdgeP(ws, row, "tttm", { grey: true });
    const current = group.data?.currentFinancialYear || {};
    const previous = group.data?.previousFinancialYear || {};
    const curTotal = formatNumber(current?.totalEmployeesCategory);
    const curUnion = formatNumber(current?.NoofEmployeesCategory);
    const prevTotal = formatNumber(previous?.totalEmployeesCategory);
    const prevUnion = formatNumber(previous?.NoofEmployeesCategory);
    // Band totals: all grey
    [
      [COL_E, curTotal],
      [COL_G, curUnion],
      [COL_I, formatRatio(current?.totalEmployeesPercentage)],
      [COL_K, prevTotal],
      [COL_M, prevUnion],
      [COL_O, formatRatio(previous?.totalEmployeesPercentage)],
    ].forEach(([col, value]) => {
      setCell(ws, row, col, value, {
        align: "center",
        fill: NA_FILL,
        bordered: false,
        border: parseBorderSpec(THIN_ALL),
      });
      setCell(ws, row, col + 1, "", {
        fill: NA_FILL,
        bordered: false,
        border: parseBorderSpec(col === COL_O ? "tttm" : THIN_ALL),
      });
      padMergeRow(ws, row, col, col + 1);
    });
    row += 1;

    ["Male", "Female", "Other"].forEach((label) => {
      const key = label.toLowerCase();
      const [tKey, uKey, pKey] = group.keys[key];
      setCell(ws, row, COL_D, label, {
        align: "left",
        bordered: false,
        border: parseBorderSpec("tmt-"),
      });
      const c = group.data?.currentFinancialYear || {};
      const p = group.data?.previousFinancialYear || {};
      let cPct = c?.[pKey];
      if (!cPct && c?.[tKey] && c?.[uKey]) {
        cPct = ((Number(c[uKey]) / Number(c[tKey])) * 100).toFixed(4);
      }
      let pPct = p?.[pKey];
      if (!pPct && p?.[tKey] && p?.[uKey]) {
        pPct = ((Number(p[uKey]) / Number(p[tKey])) * 100).toFixed(4);
      }
      // E:F linked, G:H white, I:J grey %, K:L white, M:N white, O:P grey %
      [
        [COL_E, formatNumber(c?.[tKey]), "linked"],
        [COL_G, formatNumber(c?.[uKey]), "white"],
        [COL_I, formatRatio(cPct), "grey"],
        [COL_K, formatNumber(p?.[tKey]), "white"],
        [COL_M, formatNumber(p?.[uKey]), "white"],
        [COL_O, formatRatio(pPct), "grey"],
      ].forEach(([col, value, fillKind]) => {
        if (fillKind === "linked") setLinked(ws, row, col, value, parseBorderSpec("ttt-"));
        else if (fillKind === "white") setWhite(ws, row, col, value, parseBorderSpec("ttt-"));
        else {
          setCell(ws, row, col, value, {
            align: "center",
            fill: NA_FILL,
            bordered: false,
            border: parseBorderSpec("ttt-"),
          });
        }
        setCell(ws, row, col + 1, "", {
          fill: fillKind === "linked" ? LINKED_FILL : fillKind === "white" ? VALUE_FILL : NA_FILL,
          bordered: false,
          border: parseBorderSpec(col === COL_O ? "t-tm" : "t-tt"),
        });
        padMergeRow(ws, row, col, col + 1);
        // Outer border on merge master
        const masterBorder = parseBorderSpec(col === COL_O ? "tttm" : "tttt");
        ws.getCell(row, col).border = masterBorder;
      });
      row += 1;
    });
  });

  return row;
}

function addTrainingTable(ws, row, currentEmp, previousEmp, currentWork, previousWork, fyCurrent, fyPrevious) {
  const paintHeaderGrey = (r) => {
    for (let col = COL_D; col <= COL_P; col += 1) ws.getCell(r, col).fill = NA_FILL;
  };

  mergeBlock(ws, row, COL_D, row + 2, COL_D, "Category", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("-mtt"),
  });
  mergeBlock(ws, row, COL_E, row, COL_I, fyCurrent, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("-ttt"),
  });
  mergeBlock(ws, row, COL_J, row, COL_N, fyPrevious, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("-ttt"),
  });
  paintHeaderGrey(row);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  setCell(ws, row, COL_E, "Total (A)", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, row, COL_F, row, COL_G, "On Health\nand safety measures", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, row, COL_H, row, COL_I, "On Skill upgradation", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, row, COL_J, row + 1, COL_J, "Total (D)", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, row, COL_K, row, COL_L, "On Health and safety measures", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, row, COL_M, row, COL_N, "On Skill upgradation", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  paintHeaderGrey(row);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  mergeBlock(ws, row, COL_E, row, COL_E, "Total (A)", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  [
    [COL_F, "No.(B)"],
    [COL_G, "% (B/ A)"],
    [COL_H, "No.(C)"],
    [COL_I, "% (C /A)"],
    [COL_K, "No.(E)"],
    [COL_L, "% (E / D)"],
    [COL_M, "No. (F)"],
    [COL_N, "% (F / D)"],
  ].forEach(([col, label]) => {
    setCell(ws, row, col, label, {
      align: "center",
      fill: NA_FILL,
      bordered: false,
      border: parseBorderSpec(THIN_ALL),
    });
  });
  paintHeaderGrey(row);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  const writeGroup = (bandLabel, current, previous) => {
    const bandSpecs = outerMergeBorders(COL_N, {
      first: "tmt-",
      middle: "t---",
      last: "t--t",
      edge: "---m",
    });
    writeMergedLabel(ws, row, COL_D, COL_N, bandLabel, bandSpecs);
    setGreyRange(ws, row, COL_O, COL_P);
    setEdgeP(ws, row, "---m", { grey: true });
    row += 1;

    const cats = [
      ["Male", "totalMale", "HealthMaleNumber", "HealthMalePercentage", "SkillMaleNumber", "SkillMalePercentage"],
      ["Female", "totalFemale", "HealthFemaleNumber", "HealthFemalePercentage", "SkillFemaleNumber", "SkillFemalePercentage"],
      ["Other", null, null, null, null, null],
      ["Total", "Total", "HealthTotal", null, "SkillTotal", null],
    ];
    cats.forEach(([label, totalKey, hsNum, hsPct, skNum, skPct]) => {
      const isTotal = label === "Total";
      setCell(ws, row, COL_D, label, {
        align: "left",
        bordered: false,
        border: parseBorderSpec("tmt-"),
      });
      // E W F W G G H W I G J W K W L G M W N G + O:P G
      const cells = [
        [COL_E, formatNumber(current?.[totalKey]), "white"],
        [COL_F, formatNumber(current?.[hsNum]), "white"],
        [COL_G, formatRatio(current?.[hsPct]), "grey"],
        [COL_H, formatNumber(current?.[skNum]), "white"],
        [COL_I, formatRatio(current?.[skPct]), "grey"],
        [COL_J, formatNumber(previous?.[totalKey]), "white"],
        [COL_K, formatNumber(previous?.[hsNum]), "white"],
        [COL_L, formatRatio(previous?.[hsPct]), "grey"],
        [COL_M, formatNumber(previous?.[skNum]), "white"],
        [COL_N, formatRatio(previous?.[skPct]), "grey"],
      ];
      cells.forEach(([col, value, fillKind]) => {
        if (isTotal || fillKind === "grey") {
          setCell(ws, row, col, isTotal ? "" : value, {
            align: "center",
            fill: NA_FILL,
            bordered: false,
            border: parseBorderSpec(THIN_ALL),
          });
        } else {
          setWhite(ws, row, col, value, parseBorderSpec(THIN_ALL));
        }
      });
      setGreyRange(ws, row, COL_O, COL_P);
      setEdgeP(ws, row, "---m", { grey: true });
      row += 1;
    });
  };

  writeGroup("Employees", currentEmp, previousEmp);
  writeGroup("Workers", currentWork, previousWork);
  return row;
}

function addPerformanceTable(ws, row, empCurrent, empPrevious, workCurrent, workPrevious, fyCurrent, fyPrevious) {
  const paintHeaderGrey = (r) => {
    for (let col = COL_D; col <= COL_P; col += 1) ws.getCell(r, col).fill = NA_FILL;
  };

  mergeBlock(ws, row, COL_D, row + 1, COL_D, "Category", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("tm-t"),
  });
  mergeBlock(ws, row, COL_E, row, COL_G, fyCurrent, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("ttt-"),
  });
  mergeBlock(ws, row, COL_H, row, COL_J, fyPrevious, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("t-tt"),
  });
  paintHeaderGrey(row);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  [
    [COL_E, "Total (A)"],
    [COL_F, "No.(B)"],
    [COL_G, "% (B/ A)"],
    [COL_H, "Total (D)"],
    [COL_I, "No.(E)"],
    [COL_J, "% (E / D)"],
  ].forEach(([col, label]) => {
    setCell(ws, row, col, label, {
      align: "center",
      fill: NA_FILL,
      bordered: false,
      border: parseBorderSpec(THIN_ALL),
    });
  });
  paintHeaderGrey(row);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  const writeGroup = (band, current, previous) => {
    const bandSpecs = outerMergeBorders(COL_J, {
      first: "tmt-",
      middle: "t---",
      last: "t--t",
      edge: "---m",
    });
    writeMergedLabel(ws, row, COL_D, COL_J, band, bandSpecs, { fill: NA_FILL });
    for (let col = COL_D; col <= COL_J; col += 1) ws.getCell(row, col).fill = NA_FILL;
    setGreyRange(ws, row, COL_K, COL_P);
    setEdgeP(ws, row, "---m", { grey: true });
    row += 1;
    ["Male", "Female", "Other", "Total"].forEach((label) => {
      const key = label.toLowerCase();
      const isTotal = label === "Total";
      setCell(ws, row, COL_D, label, {
        align: "left",
        bordered: false,
        border: parseBorderSpec("tmt-"),
      });
      // E linked, F white, G grey %, H grey (prev total), I white, J grey %, K:P grey
      [
        [COL_E, formatNumber(current?.totalEmployee?.[key]), "linked"],
        [COL_F, formatNumber(current?.employee?.[key]), "white"],
        [COL_G, formatRatio(current?.Percentage?.[key]), "grey"],
        [COL_H, formatNumber(previous?.totalEmployee?.[key]), "grey"],
        [COL_I, formatNumber(previous?.employee?.[key]), "white"],
        [COL_J, formatRatio(previous?.Percentage?.[key]), "grey"],
      ].forEach(([col, value, fillKind]) => {
        if (isTotal || fillKind === "grey") {
          setCell(ws, row, col, isTotal ? "" : value, {
            align: "center",
            fill: NA_FILL,
            bordered: false,
            border: parseBorderSpec(THIN_ALL),
          });
        } else if (fillKind === "linked") {
          setLinked(ws, row, col, value, parseBorderSpec(THIN_ALL));
        } else {
          setWhite(ws, row, col, value, parseBorderSpec(THIN_ALL));
        }
      });
      setGreyRange(ws, row, COL_K, COL_P);
      setEdgeP(ws, row, "---m", { grey: true });
      row += 1;
    });
  };

  writeGroup("Employees", empCurrent, empPrevious);
  writeGroup("Workers", workCurrent, workPrevious);
  return row;
}

function addSafetyIncidents(ws, row, incidents, fyCurrent, fyPrevious) {
  setCell(ws, row, COL_D, "Safety Incident/Number", {
    bold: true,
    align: "center",
    bordered: false,
    border: parseBorderSpec("-mtt"),
  });
  ws.getCell(row, COL_E).value = {
    richText: [
      { text: "Category", font: { name: "Calibri", size: 11, bold: true, color: { argb: "FF000000" } } },
      { text: "*", font: { name: "Calibri", size: 11, bold: true, color: { argb: "FFFF0000" } } },
    ],
  };
  ws.getCell(row, COL_E).font = { name: "Calibri", size: 11, bold: true };
  ws.getCell(row, COL_E).alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  ws.getCell(row, COL_E).border = parseBorderSpec("-ttt");
  setCell(ws, row, COL_F, fyCurrent, {
    bold: true,
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  setCell(ws, row, COL_G, fyPrevious, {
    bold: true,
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  setGreyRange(ws, row, COL_H, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  SAFETY_INCIDENTS.forEach(([incidentLabel, key]) => {
    const empData = incidents?.employees?.[key] || {};
    const workData = incidents?.workers?.[key] || {};
    mergeBlock(ws, row, COL_D, row + 1, COL_D, incidentLabel, {
      align: "left",
      vertical: "top",
      bordered: false,
      border: parseBorderSpec("tmtt"),
    });
    setCell(ws, row, COL_E, "Employees", {
      align: "center",
      bordered: false,
      border: parseBorderSpec(THIN_ALL),
    });
    setWhite(ws, row, COL_F, formatNumber(empData?.currentFinancialYear), parseBorderSpec(THIN_ALL));
    setWhite(ws, row, COL_G, formatNumber(empData?.previousFinancialYear), parseBorderSpec("t-tt"));
    setGreyRange(ws, row, COL_H, COL_P);
    setEdgeP(ws, row, "---m", { grey: true });
    row += 1;
    setCell(ws, row, COL_E, "Workers", {
      align: "center",
      bordered: false,
      border: parseBorderSpec(THIN_ALL),
    });
    setWhite(ws, row, COL_F, formatNumber(workData?.currentFinancialYear), parseBorderSpec(THIN_ALL));
    setWhite(ws, row, COL_G, formatNumber(workData?.previousFinancialYear), parseBorderSpec("t-tt"));
    setGreyRange(ws, row, COL_H, COL_P);
    if (key === "highConsequenceInjuries") {
      ws.mergeCells(row, COL_H, row, COL_J);
      ws.getCell(row, COL_H).value = {
        richText: [
          {
            text: "*Including the contract workforce",
            font: { name: "Calibri", size: 11, color: { argb: "FFFF0000" } },
          },
        ],
      };
      ws.getCell(row, COL_H).font = { name: "Calibri", size: 11, color: { argb: "FFFF0000" } };
      ws.getCell(row, COL_H).alignment = { horizontal: "left", vertical: "middle", wrapText: true };
    }
    setEdgeP(ws, row, "---m", { grey: true });
    row += 1;
  });

  return row;
}

function addComplaintsTable(ws, row, essential, fyCurrent, fyPrevious) {
  // R124 FY headers
  setCell(ws, row, COL_D, "", { bordered: false, border: parseBorderSpec("tmtt") });
  mergeBlock(ws, row, COL_E, row, COL_G, fyCurrent, {
    bold: true,
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, row, COL_H, row, COL_J, fyPrevious, {
    bold: true,
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  setGreyRange(ws, row, COL_K, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  setCell(ws, row, COL_D, "", { bordered: false, border: parseBorderSpec("tmtt") });
  [
    [COL_E, "Filed during the year"],
    [COL_F, "Pending resolution at the end of year"],
    [COL_G, "Remarks"],
    [COL_H, "Filed during the year"],
    [COL_I, "Pending resolution at the end of year"],
    [COL_J, "Remarks"],
  ].forEach(([col, label]) => {
    setCell(ws, row, col, label, {
      align: "center",
      bordered: false,
      border: parseBorderSpec(col === COL_F || col === COL_I ? "ttt-" : THIN_ALL),
    });
  });
  ws.getRow(row).height = 43.5;
  setGreyRange(ws, row, COL_K, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  const current = essential?.complaintsMadeByEmployeesAndWorkers?.currentFinancialYear || {};
  const previous = essential?.complaintsMadeByEmployeesAndWorkers?.previousFinancialYear || {};
  [
    ["Working Conditions", "workingCondition"],
    ["Health & Safety", "healthAndSafety"],
  ].forEach(([label, key]) => {
    const c = current?.[key] || {};
    const p = previous?.[key] || {};
    setCell(ws, row, COL_D, label, {
      align: "left",
      bordered: false,
      border: parseBorderSpec("tmtt"),
    });
    setWhite(ws, row, COL_E, formatNumber(c?.filled), parseBorderSpec(THIN_ALL));
    setWhite(ws, row, COL_F, formatNumber(c?.pending), parseBorderSpec("ttt-"));
    setWhite(ws, row, COL_G, safeText(c?.remark), parseBorderSpec(THIN_ALL), "left");
    setWhite(ws, row, COL_H, formatNumber(p?.filled), parseBorderSpec("t-tt"));
    setWhite(ws, row, COL_I, formatNumber(p?.pending), parseBorderSpec("ttt-"));
    setWhite(ws, row, COL_J, safeText(p?.remark), parseBorderSpec(THIN_ALL), "left");
    setGreyRange(ws, row, COL_K, COL_P);
    setEdgeP(ws, row, "---m", { grey: true });
    row += 1;
  });

  return row;
}

function addAssessmentTable(ws, row, headerLabel, rows) {
  setCell(ws, row, COL_D, "", { bordered: false, border: parseBorderSpec("tmtt") });
  mergeBlock(ws, row, COL_E, row, COL_G, headerLabel, {
    align: "center",
    bordered: false,
    border: parseBorderSpec("t-tt"),
  });
  setGreyRange(ws, row, COL_H, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  ws.getRow(row).height = 29.25;
  row += 1;

  rows.forEach(([label, value]) => {
    setCell(ws, row, COL_D, label, {
      align: "left",
      bordered: false,
      border: parseBorderSpec("tmtt"),
    });
    setWhite(ws, row, COL_E, formatPercent(value), parseBorderSpec("ttt-"));
    setCell(ws, row, COL_F, "", { bordered: false, border: parseBorderSpec("t-t-") });
    setCell(ws, row, COL_G, "", { bordered: false, border: parseBorderSpec("t-tt") });
    padMergeRow(ws, row, COL_E, COL_G);
    setGreyRange(ws, row, COL_H, COL_P);
    setEdgeP(ws, row, "---m", { grey: true });
    row += 1;
  });
  return row;
}

function addRehabilitationTable(ws, row, leadership, fyCurrent, fyPrevious) {
  setCell(ws, row, COL_D, "", { bordered: false, border: parseBorderSpec("tm-t") });
  mergeBlock(ws, row, COL_E, row, COL_H, "Total no. of affected employees/ workers", {
    bold: true,
    align: "center",
    bordered: false,
    border: parseBorderSpec("t-tt"),
  });
  mergeBlock(
    ws,
    row,
    COL_I,
    row,
    COL_L,
    "No. of employees/workers that are rehabilitated and placed in suitable employment or whose family members have been placed in suitable employment",
    {
      bold: true,
      align: "center",
      bordered: false,
      border: parseBorderSpec("t-tt"),
    }
  );
  ws.getRow(row).height = 45;
  setGreyRange(ws, row, COL_M, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  setCell(ws, row, COL_D, "", { bordered: false, border: parseBorderSpec("-mtt") });
  [
    [COL_E, fyCurrent],
    [COL_G, fyPrevious],
    [COL_I, fyCurrent],
    [COL_K, fyPrevious],
  ].forEach(([col, label]) => {
    mergeBlock(ws, row, col, row, col + 1, label, {
      bold: true,
      align: "center",
      bordered: false,
      border: parseBorderSpec(col === COL_K ? "t-tt" : "ttt-"),
    });
  });
  setGreyRange(ws, row, COL_M, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  const current = leadership?.rehabilitatedEmploymentDetails?.currentFinancialYear || {};
  const previous = leadership?.rehabilitatedEmploymentDetails?.previousFinancialYear || {};
  [
    ["Employees", "employee"],
    ["Workers", "workers"],
  ].forEach(([label, key]) => {
    setCell(ws, row, COL_D, label, {
      align: "left",
      bordered: false,
      border: parseBorderSpec("tmtt"),
    });
    [
      [COL_E, formatNumber(current?.[key]?.total)],
      [COL_G, formatNumber(previous?.[key]?.total)],
      [COL_I, formatNumber(current?.[key]?.rehabiliatated)],
      [COL_K, formatNumber(previous?.[key]?.rehabiliatated)],
    ].forEach(([col, value]) => {
      setWhite(ws, row, col, value, parseBorderSpec("ttt-"));
      setCell(ws, row, col + 1, "", {
        bordered: false,
        border: parseBorderSpec("t-tt"),
      });
      padMergeRow(ws, row, col, col + 1);
    });
    setGreyRange(ws, row, COL_M, COL_P);
    setEdgeP(ws, row, "---m", { grey: true });
    row += 1;
  });

  return row;
}

function addNotesRow(ws, row) {
  const specs = ["tmmt", "ttmt", "ttm-", "t-m-", "t-m-", "t-m-", "t-m-", "t-m-", "t-m-", "--m-", "--m-", "--m-", "--mm"];
  setCell(ws, row, COL_D, "Notes", {
    align: "left",
    bordered: false,
    border: borderFromSpecs(specs, COL_D),
  });
  setCell(ws, row, COL_E, "", {
    bordered: false,
    border: borderFromSpecs(specs, COL_E),
    fill: VALUE_FILL,
  });
  for (let col = COL_F; col <= COL_P; col += 1) {
    setCell(ws, row, col, "", {
      fill: NA_FILL,
      bordered: false,
      border: borderFromSpecs(specs, col) || parseBorderSpec(col === COL_P ? "--mm" : "--m-"),
    });
  }
  ws.getRow(row).height = 30;
}

export function buildPrinciple3Sheet(workbook, sectionC, reportingYear = "") {
  const ws = workbook.addWorksheet("PRINCIPLE 3");
  setPrinciple3ColumnWidths(ws);
  setupPrinciple3SheetView(workbook, ws);
  hideRowsThrough(ws, 5);

  const principle = sectionC?.principle3 || {};
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const fyLabels = getFinancialYearLabels(reportingYear);
  const fyCurrent = fyLabels.currentYear ? `FY (${fyLabels.currentYear})` : "FY (Current Financial Year)";
  const fyPrevious = fyLabels.previousYear ? `PY (${fyLabels.previousYear})` : "PY (Previous Financial Year)";

  addPrincipleTitle(ws);
  let row = addSectionBand(ws, 9, "Essential Indicators");

  // R10 Q1a
  const q1aSpecs = outerMergeBorders(COL_O, {
    first: "-mtt",
    middle: "-ttt",
    last: "-ttt",
    edge: "---m",
  });
  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_O,
    "1. a. Details of measures for the well-being of employees:",
    q1aSpecs,
    { bold: true, edgeGrey: true }
  );
  row += 1;

  row = addWellBeingHeader(ws, row, "% of employees covered by");
  row = addWellBeingBlock(ws, row, essential?.wellBeingMeasures, {
    sectionKey: "permanentEmployees",
    maleCountKey: "permanentEmployees",
    femaleCountKey: "permanentEmployees",
    blockLabel: "Permanent employees",
  });
  row = addWellBeingBlock(ws, row, essential?.wellBeingMeasures, {
    sectionKey: "otherEmployees",
    maleCountKey: "otherEmployees",
    femaleCountKey: "otherEmployees",
    blockLabel: "Other than permanent employees",
  });

  // R24 workers intro
  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_O,
    "b. Details of measures for the well-being of workers:",
    outerMergeBorders(COL_O, {
      first: "tmtt",
      middle: "-ttt",
      last: "-ttt",
      edge: "---m",
    }),
    { bold: true, edgeGrey: true }
  );
  row += 1;

  row = addWellBeingHeader(ws, row, "% of workers covered by");
  row = addWellBeingBlock(ws, row, essential?.wellBeingWorkers, {
    sectionKey: "permanentWorkers",
    maleCountKey: "permanentWorkers",
    femaleCountKey: "permanentWorkers",
    blockLabel: "Permanent workers",
  });
  row = addWellBeingBlock(ws, row, essential?.wellBeingWorkers, {
    sectionKey: "otherWorkers",
    maleCountKey: "otherWorkers",
    femaleCountKey: "otherWorkers",
    blockLabel: "Other than permanent workers",
  });

  row = addSpendingSection(ws, row, essential, fyCurrent, fyPrevious);
  row = addRetirementSection(ws, row, essential, fyCurrent, fyPrevious);

  // R50 Accessibility
  writeMergedLabel(ws, row, COL_D, COL_P, "3. Accessibility of workplaces", dpSectionBorders(), {
    bold: false,
  });
  row += 1;

  const access = essential?.accessibilityUnderRPWD2016 || {};
  row = addYesNoRow(
    ws,
    row,
    "Are the premises / offices of the entity accessible to differently abled employees and workers, as per the requirements of the Rights of Persons with Disabilities Act, 2016? ",
    toYesNo(access?.status),
    COL_L,
    COL_M,
    { greyAfter: true, first: "tmtt" }
  );
  row = addYesNoRow(
    ws,
    row,
    "If not, whether any steps are being taken by the entity in this regard.",
    safeText(access?.remark),
    COL_L,
    COL_M,
    {
      greyValue: !access?.remark,
      first: "tmt-",
      mid: "t-t-",
      labelLast: "t-tt",
    }
  );

  const policy = essential?.rightsOfPersonsDisability2016 || {};
  row = addYesNoRow(
    ws,
    row,
    "4. Does the entity have an equal opportunity policy as per the Rights of Persons with Disabilities Act, 2016?",
    toYesNo(policy?.status),
    COL_L,
    COL_M,
    { first: "tm-t", mid: "tt-t", labelLast: "tt-t" }
  );
  row = addYesNoRow(
    ws,
    row,
    "If so, provide a web-link to the policy.",
    safeText(policy?.webLink) || "Add Details",
    COL_L,
    COL_M,
    { first: "tmt-", mid: "t-t-", labelLast: "t-tt" }
  );

  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_L,
    "5. Return to work and Retention rates of permanent employees and workers that took parental leave.",
    outerMergeBorders(COL_L, { first: "-mtt", middle: "-ttt", last: "-ttt", edge: "---m" })
  );
  setGreyRange(ws, row, COL_M, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;
  row = addReturnRetention(ws, row, essential?.returnAndRetentionParentalLeave);

  // R62 grievance
  const grievance = essential?.grievanceRedressalMechanism || {};
  const anyYes = GRIEVANCE_SEGMENTS.some(([, key]) => {
    const code = grievance?.[key]?.code;
    return code === true || code === "true" || code === "Yes" || code === "yes";
  });
  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_K,
    "6. Is there a mechanism available to receive and redress grievances for the following categories of employees and worker?",
    outerMergeBorders(COL_K, { first: "-mtt", middle: "-ttt", last: "-ttt", edge: "---m" })
  );
  setWhite(ws, row, COL_L, anyYes ? "Yes" : toYesNo(grievance?.status) || "", parseBorderSpec("-ttt"));
  setGreyRange(ws, row, COL_M, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  setCell(ws, row, COL_D, "If yes, give details of the mechanism in brief.", {
    align: "left",
    bordered: false,
    border: parseBorderSpec("tmt-"),
  });
  setCell(ws, row, COL_E, "Yes/No\n", {
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  setCell(ws, row, COL_F, "(If Yes, then give details of the mechanism in brief)", {
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  setGreyRange(ws, row, COL_G, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  GRIEVANCE_SEGMENTS.forEach(([label, key]) => {
    const item = grievance?.[key] || {};
    setCell(ws, row, COL_D, label, {
      align: "left",
      bordered: false,
      border: parseBorderSpec("tmtt"),
    });
    setWhite(ws, row, COL_E, toYesNo(item?.code), parseBorderSpec(THIN_ALL));
    setWhite(ws, row, COL_F, safeText(item?.remark), parseBorderSpec(THIN_ALL), "left");
    setGreyRange(ws, row, COL_G, COL_P);
    setEdgeP(ws, row, "---m", { grey: true });
    row += 1;
  });

  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_P,
    "7. Membership of employees and worker in association(s) or Unions recognised by the listed entity:",
    dpSectionBorders().map((s, i) => (i === 0 ? "tmt-" : i === 12 ? "--tm" : "--t-"))
  );
  row += 1;
  row = addMembershipTable(
    ws,
    row,
    essential?.membershipInAssociationsOrUnions,
    fyCurrent,
    fyPrevious
  );

  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_P,
    "8. Details of training given to employees and workers:",
    Array.from({ length: 13 }, (_, i) => (i === 0 ? "tmt-" : i === 12 ? "--tm" : "--t-"))
  );
  row += 1;
  row = addTrainingTable(
    ws,
    row,
    essential?.trainingDetails?.currentFinancialYear?.employeeTrainingUpgradation,
    essential?.trainingDetails?.previousFinancialYear?.employeeTrainingUpgradation,
    essential?.trainingDetails?.currentFinancialYear?.workerTrainingUpgradation,
    essential?.trainingDetails?.previousFinancialYear?.workerTrainingUpgradation,
    fyCurrent,
    fyPrevious
  );

  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_J,
    "9. Details of performance and career development reviews of employees and worker:",
    outerMergeBorders(COL_J, { first: "tmt-", middle: "--t-", last: "--tt", edge: "---m" })
  );
  setGreyRange(ws, row, COL_K, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;
  row = addPerformanceTable(
    ws,
    row,
    essential?.performanceAndCareerReviews?.employee?.currentFinancialYear,
    essential?.performanceAndCareerReviews?.employee?.previousFinancialYear,
    essential?.performanceAndCareerReviews?.workers?.currentFinancialYear,
    essential?.performanceAndCareerReviews?.workers?.previousFinancialYear,
    fyCurrent,
    fyPrevious
  );

  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_J,
    "10. Health and safety management system:",
    outerMergeBorders(COL_J, { first: "tmt-", middle: "--t-", last: "--tt", edge: "---m" })
  );
  setGreyRange(ws, row, COL_K, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  const hs = essential?.healthAndSafetyManagement || {};
  row = addYesNoRow(
    ws,
    row,
    "a.Whether an occupational health and safety management system has been implemented by the entity? (Yes/ No/ NA).",
    toYesNo(hs?.occupationalHealthAndSafety?.status),
    COL_H,
    COL_I,
    { greyAfter: true, first: "tmt-", mid: "t-t-", labelLast: "t-tt", height: 30 }
  );
  row = addYesNoRow(
    ws,
    row,
    " If yes, the coverage such system?",
    safeText(hs?.occupationalHealthAndSafety?.remark),
    COL_H,
    COL_I,
    { first: "tmt-", mid: "t-t-", labelLast: "t-tt" }
  );
  row = addYesNoRow(
    ws,
    row,
    "b. What are the processes used to identify work-related hazards and assess risks on a routine and non-routine basis by the entity?",
    safeText(hs?.workRelatedHazardAssessment?.remark),
    COL_H,
    COL_I,
    { first: "tmt-", mid: "t-t-", labelLast: "t-tt" }
  );
  row = addYesNoRow(
    ws,
    row,
    "c. Whether you have processes for workers to report the work related hazards and to remove themselves from such risks?",
    toYesNo(hs?.hazardReportingAndSelfRemovalProcess?.status),
    COL_H,
    COL_I,
    { greyAfter: true, first: "tmt-", mid: "t-t-", labelLast: "t-tt" }
  );
  row = addYesNoRow(
    ws,
    row,
    "d. Do the employees/ worker of the entity have access to non-occupational medical and healthcare services?",
    toYesNo(hs?.nonOccupationalMedicalServicesAccess?.status),
    COL_H,
    COL_I,
    { greyAfter: true, first: "tmt-", mid: "t-t-", labelLast: "t-tt" }
  );

  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_G,
    "11. Details of safety related incidents, in the following format:",
    outerMergeBorders(COL_G, { first: "tmt-", middle: "t-t-", last: "t-tt", edge: "---m" })
  );
  setGreyRange(ws, row, COL_H, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;
  row = addSafetyIncidents(ws, row, essential?.safetyIncidents, fyCurrent, fyPrevious);

  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_I,
    "12. Describe the measures taken by the entity to ensure a safe and healthy work place.",
    outerMergeBorders(COL_I, { first: "tmtt", middle: THIN_ALL, last: THIN_ALL, edge: "---m" })
  );
  setWhite(ws, row, COL_J, safeText(essential?.safetyMeasures), parseBorderSpec(THIN_ALL), "left");
  setGreyRange(ws, row, COL_K, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  ws.getRow(row).height = 30;
  row += 1;

    writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_J,
    "13. Number of Complaints on the following made by employees and workers:",
    outerMergeBorders(COL_J, { first: "tmtt", middle: THIN_ALL, last: THIN_ALL, edge: "---m" })
  );
  // setCell(ws, row, COL_D, "13. Number of Complaints on the following made by employees and workers:", {
  //   align: "left",
  //   bordered: false,
  //   border: parseBorderSpec("-m--"),
  // });
  setGreyRange(ws, row, COL_K, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;
  row = addComplaintsTable(ws, row, essential, fyCurrent, fyPrevious);

  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_G,
    "14. Assessments for the year:",
    outerMergeBorders(COL_G, { first: "tmt-", middle: "t-t-", last: "t-t-", edge: "---m" })
  );
  setGreyRange(ws, row, COL_H, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;
  row = addAssessmentTable(
    ws,
    row,
    "% of your plants and offices that were assessed (by entity or statutory authorities or third parties)",
    [
      ["Health and safety practices", essential?.assessmentYear?.healthAndSafetyPractice],
      ["Working Conditions", essential?.assessmentYear?.workingCondition],
    ]
  );

  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_I,
    "15. Provide details of any corrective action taken or underway to address safety-related incidents (if any) and on significant risks / concerns arising from assessments of health & safety practices and working conditions.",
    outerMergeBorders(COL_I, { first: "tmt-", middle: "t-t-", last: "t-tt", edge: "--tm" })
  );
  setWhite(
    ws,
    row,
    COL_J,
    safeText(essential?.healthAndSafetyPracticesWorking),
    parseBorderSpec(THIN_ALL),
    "left"
  );
  setGreyRange(ws, row, COL_K, COL_P);
  setEdgeP(ws, row, "--tm", { grey: true });
  ws.getRow(row).height = 30;
  row += 1;

  row = addSectionBand(ws, row, "Leadership Indicators");

  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_L,
    "1. Does the entity extend any life insurance or any compensatory package in the event of death of ",
    outerMergeBorders(COL_L, { first: "-mtt", middle: "-ttt", last: "-ttt", edge: "---m" })
  );
  setGreyRange(ws, row, COL_M, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  const lifeInsurance = leadership?.lifeInsuranceOrCompensatoryPackage || {};
  row = addYesNoRow(
    ws,
    row,
    "(A) Employees (Y/N)",
    safeText(lifeInsurance?.male ?? lifeInsurance?.employee) ||
      toYesNo(lifeInsurance?.male ?? lifeInsurance?.employee),
    COL_K,
    COL_L,
    { first: "tmt-", mid: "t-t-", labelLast: "t-tt" }
  );
  row = addYesNoRow(
    ws,
    row,
    "(B) Workers (Y/N).",
    safeText(lifeInsurance?.female ?? lifeInsurance?.workers) ||
      toYesNo(lifeInsurance?.female ?? lifeInsurance?.workers),
    COL_K,
    COL_L,
    { first: "tmt-", mid: "t-t-", labelLast: "t-tt" }
  );

  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_K,
    "2. Provide the measures undertaken by the entity to ensure that statutory dues have been deducted and deposited by the value chain partners.",
    outerMergeBorders(COL_K, { first: "tmt-", middle: "t-t-", last: "t-tt", edge: "---m" })
  );
  setWhite(
    ws,
    row,
    COL_L,
    safeText(leadership?.statutoryDuesDeductedDeposited),
    parseBorderSpec(THIN_ALL),
    "left"
  );
  setGreyRange(ws, row, COL_M, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_L,
    "3. Provide the number of employees / workers having suffered high consequence work related injury / ill-health / fatalities (as reported in Q11 of Essential Indicators above), who have been are rehabilitated and placed in suitable employment or whose family members have been placed in suitable employment:",
    outerMergeBorders(COL_L, { first: "tmt-", middle: "t-t-", last: "t-tt", edge: "---m" }),
    { height: 30 }
  );
  setGreyRange(ws, row, COL_M, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;
  row = addRehabilitationTable(ws, row, leadership, fyCurrent, fyPrevious);

  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_J,
    "4. Does the entity provide transition assistance programs to facilitate continued employability and the management of career endings resulting from retirement or termination of employment? (Yes/ No/ NA)",
    outerMergeBorders(COL_J, { first: "tmt-", middle: "t-t-", last: "t-tt", edge: "---m" })
  );
  setWhite(
    ws,
    row,
    COL_K,
    safeText(leadership?.employabilityandmanagement) ||
      toYesNo(leadership?.employabilityandmanagement),
    parseBorderSpec(THIN_ALL)
  );
  setGreyRange(ws, row, COL_L, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_G,
    "5. Details on assessment of value chain partners: ",
    outerMergeBorders(COL_G, { first: "tmt-", middle: "t-t-", last: "t-tt", edge: "---m" })
  );
  setGreyRange(ws, row, COL_H, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;
  row = addAssessmentTable(
    ws,
    row,
    "% of value chain partners (by value of business done with such partners) that were assessed",
    [
      [
        "Health and safety practices",
        leadership?.assessmentOfValueChainPartners?.healthAndSafetyPractice ??
          leadership?.assessmentYear?.healthAndSafetyPractice,
      ],
      [
        "Working Conditions",
        leadership?.assessmentOfValueChainPartners?.workingCondition ??
          leadership?.assessmentYear?.workingCondition,
      ],
    ]
  );

  writeMergedLabel(
    ws,
    row,
    COL_D,
    COL_K,
    "6. Provide details of any corrective actions taken or underway to address significant risks / concerns arising from assessments of health and safety practices and working conditions of value chain partners.",
    outerMergeBorders(COL_K, { first: "tmt-", middle: "t-t-", last: "t-tt", edge: "---m" }),
    { height: 30 }
  );
  setWhite(ws, row, COL_L, safeText(leadership?.valuechainpartners), parseBorderSpec(THIN_ALL), "left");
  setGreyRange(ws, row, COL_M, COL_P);
  setEdgeP(ws, row, "---m", { grey: true });
  row += 1;

  addNotesRow(ws, row);

  return ws;
}
